import React, { useState, useEffect, useCallback, forwardRef } from 'react'
import { set } from 'sanity'
import { useClient, useFormValue } from 'sanity'
import { ObjectInputProps } from 'sanity'
import {
  Stack,
  Heading,
  Grid,
  Select,
  Flex,
  Spinner,
  Box,
  Text,
  Card,
  Button,
  Label,
} from '@sanity/ui'
import axios from 'axios'

interface WheelConfiguration {
  diameter: string
  width: string
  offset: string
}

interface MagentoProduct {
  attributes?: {
    apex_wheel_diameter?: string
    apex_wheel_width?: string
    apex_wheel_offset?: string
  }
}

interface WidthDocument {
  _id: string
  name: string
}

interface WheelSizeValue {
  diameter?: {
    name: string
    wheelDiameter: number
  }
  width?: {
    _type: 'reference'
    _ref: string
  }
  offset?: {
    name: string
  }
  alternativeOffset?: {
    name: string
  }
}

const fetchProducts = async (
  skus: string[],
  baseURL: string,
  retryCount = 0,
): Promise<MagentoProduct[]> => {
  const MAX_RETRIES = 3
  const RETRY_DELAY = 1000 * Math.pow(2, retryCount) // Exponential backoff

  const axiosInstance = axios.create({
    baseURL,
    timeout: 30000, // 30 second timeout
  })

  try {
    const { data } = await axiosInstance.request({
      method: 'POST',
      url: `/api/callbacks/search-products?sku=${skus.join(',')}`,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
    })
    return data
  } catch (error: any) {
    // Check if we should retry
    if (retryCount < MAX_RETRIES) {
      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
      return fetchProducts(skus, baseURL, retryCount + 1)
    }

    // After all retries failed, throw a more descriptive error
    const errorMessage =
      error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED'
        ? 'Cannot connect to the product service. Please ensure the frontend server is running at ' +
          baseURL
        : error.response?.status === 404
          ? 'Product search endpoint not found. Please check the API configuration.'
          : error.response?.status >= 500
            ? 'Server error occurred while fetching products. Please try again later.'
            : `Failed to fetch products: ${error.message}`

    throw new Error(errorMessage)
  }
}

const WheelSizeSelector = forwardRef<HTMLDivElement, ObjectInputProps>((props, ref) => {
  const { schemaType, value, onChange } = props

  // Get document from form context
  const document = useFormValue([]) as any

  const typedValue = value as WheelSizeValue | undefined

  const [configurations, setConfigurations] = useState<WheelConfiguration[]>([])
  const [diameters, setDiameters] = useState<string[]>([])
  const [widths, setWidths] = useState<string[]>([])
  const [offsets, setOffsets] = useState<string[]>([])
  const [alternativeOffsets, setAlternativeOffsets] = useState<string[]>([])

  const [selectedDiameter, setSelectedDiameter] = useState<string>('')
  const [selectedWidth, setSelectedWidth] = useState<string>('')
  const [selectedOffset, setSelectedOffset] = useState<string>('')
  const [selectedAlternativeOffset, setSelectedAlternativeOffset] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryAttempt, setRetryAttempt] = useState(0)
  const [widthDocuments, setWidthDocuments] = useState<Map<string, WidthDocument>>(
    new Map(),
  )

  const client = useClient({ apiVersion: '2023-05-03' })

  const selectDiameters = useCallback((data: WheelConfiguration[]) => {
    const output = data.map(({ diameter }) => diameter)
    const uniqueSorted = [...new Set(output)].sort(
      (a, b) => parseFloat(a) - parseFloat(b),
    )
    setDiameters(uniqueSorted)
  }, [])

  const selectWidths = useCallback((data: WheelConfiguration[]) => {
    const output = data.map(({ width }) => width)
    const uniqueSorted = [...new Set(output)].sort(
      (a, b) => parseFloat(a) - parseFloat(b),
    )
    setWidths(uniqueSorted)
  }, [])

  const selectOffsets = useCallback((data: WheelConfiguration[]) => {
    const output = [...new Set(data.map(({ offset }) => offset))].sort()
    setOffsets(output)
  }, [])

  const selectAlternativeOffsets = useCallback(
    (data: WheelConfiguration[], selected: string) => {
      const allOffsets = [...new Set(data.map(({ offset }) => offset))]
      const alternative = allOffsets.filter((offset) => offset !== selected).sort()
      setAlternativeOffsets(alternative)
    },
    [],
  )

  const selectWidth = useCallback(
    (data: string) => {
      setSelectedWidth(data)
      setSelectedOffset('')
      const filteredConfigs = configurations.filter(
        ({ diameter, width }) => selectedDiameter === diameter && data === width,
      )
      selectOffsets(filteredConfigs)
    },
    [configurations, selectedDiameter, selectOffsets],
  )

  const selectDiameter = useCallback(
    (data: string) => {
      setSelectedDiameter(data)
      selectWidth('')
      const filteredConfigs = configurations.filter(({ diameter }) => data === diameter)
      selectWidths(filteredConfigs)
    },
    [configurations, selectWidth, selectWidths],
  )

  /**
   * Find width document by matching Magento width value to Sanity width names
   * Magento returns values like: 9.0, 9.5, 10.0
   * Sanity width documents might have names like: 9.0, 9", 9.0", 9
   */
  const findWidthDocumentByValue = useCallback(
    (magentoWidth: string): WidthDocument | null => {
      if (!magentoWidth || !widthDocuments.size) return null

      // First try exact match
      let widthDoc = [...widthDocuments.values()].find((w) => w.name === magentoWidth)
      if (widthDoc) {
        return widthDoc
      }

      // Extract numeric value for comparison
      const numericValue = parseFloat(magentoWidth)
      if (isNaN(numericValue)) {
        return null
      }

      // Try different format variations that might exist in Sanity
      const variations = [
        magentoWidth, // Original (e.g., "9.0")
        `${numericValue}"`, // With quotes (e.g., "9"")
        `${numericValue}`, // Plain number (e.g., "9")
        numericValue.toFixed(1), // Decimal format (e.g., "9.0")
        `${numericValue.toFixed(1)}"`, // Decimal with quotes (e.g., "9.0"")
        numericValue % 1 === 0 ? `${Math.floor(numericValue)}` : `${numericValue}`, // Integer or float
      ]

      // Try each variation
      for (const variant of variations) {
        widthDoc = [...widthDocuments.values()].find((w) => w.name === variant)
        if (widthDoc) {
          return widthDoc
        }
      }

      // If still not found, try loose numeric matching
      for (const width of widthDocuments.values()) {
        const storedNumeric = parseFloat(width.name)
        if (!isNaN(storedNumeric) && Math.abs(storedNumeric - numericValue) < 0.01) {
          return width
        }
      }

      return null
    },
    [widthDocuments],
  )

  const formatPayload = useCallback(
    (
      diameter: string,
      width: string,
      offset: string,
      alternativeOffset?: string,
    ): WheelSizeValue => {
      // Find width reference using translation logic for Magento values
      let widthRef = null
      if (width) {
        const widthDoc = findWidthDocumentByValue(width)
        if (widthDoc) {
          widthRef = { _type: 'reference' as const, _ref: widthDoc._id }
        }
      }

      return {
        diameter: {
          name: diameter,
          wheelDiameter: parseInt(diameter),
        },
        width: widthRef || undefined,
        offset: {
          name: offset,
        },
        alternativeOffset: alternativeOffset
          ? {
              name: alternativeOffset,
            }
          : undefined,
      }
    },
    [findWidthDocumentByValue],
  )

  const selectOffset = useCallback(
    (data: string) => {
      setSelectedOffset(data)
      // Reset alternative offset when offset changes
      setSelectedAlternativeOffset('')
      onChange(set(formatPayload(selectedDiameter, selectedWidth, data)))
      const filteredData = configurations.filter(
        ({ diameter, width }) =>
          selectedDiameter === diameter && selectedWidth === width,
      )
      selectAlternativeOffsets(filteredData, data)
    },
    [
      selectedDiameter,
      selectedWidth,
      configurations,
      formatPayload,
      onChange,
      selectAlternativeOffsets,
    ],
  )

  const selectAlternativeOffset = useCallback(
    (data: string) => {
      setSelectedAlternativeOffset(data)
      onChange(set(formatPayload(selectedDiameter, selectedWidth, selectedOffset, data)))
    },
    [selectedDiameter, selectedWidth, selectedOffset, formatPayload, onChange],
  )

  useEffect(() => {
    if (widths.length === 1) {
      selectWidth(widths[0])
    }
  }, [widths, selectWidth])

  useEffect(() => {
    if (offsets.length === 1) {
      selectOffset(offsets[0])
    }
  }, [offsets, selectOffset])

  const loadWheelData = useCallback(async () => {
    const wheelConfiguratorQuery = `*[_type == "generation" && references($id)][0] {
      ...configuration->{configuration}
    }`

    setIsLoading(true)
    setError(null)

    try {
      // Load width documents first
      const widthDocs = await client.fetch<WidthDocument[]>(
        '*[_type == "width"]{_id, name}',
      )
      const widthMap = new Map<string, WidthDocument>()
      widthDocs.forEach((width) => {
        widthMap.set(width.name, width)
      })
      setWidthDocuments(widthMap)

      // Fetch configuration from Sanity
      const result = await client.fetch(wheelConfiguratorQuery, {
        id: document._id.replace('drafts.', ''),
      })

      if (!result?.configuration) {
        throw new Error(
          'No wheel configuration found. This buyer guide must be linked to a vehicle generation that has a wheel configuration. ' +
            'Go to the generation document and set the "Buyer guide" field to reference this document.',
        )
      }

      // Extract SKUs and fetch products
      const skus = result.configuration.map(({ sku }: any) => sku)

      if (skus.length === 0) {
        throw new Error('No wheel SKUs found in configuration')
      }

      // Get frontend URL from environment or use default
      const baseURL =
        process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'

      const products = await fetchProducts(skus, baseURL)

      if (!products || products.length === 0) {
        throw new Error('No product data returned from the API')
      }

      // Process the product data
      const data = products
        .map((product) => {
          return {
            diameter: product.attributes?.apex_wheel_diameter || '',
            width: product.attributes?.apex_wheel_width || '',
            offset: product.attributes?.apex_wheel_offset || '',
          }
        })
        .filter((item) => {
          return item.diameter && item.width && item.offset
        })

      if (data.length === 0) {
        throw new Error('No valid wheel data found in products')
      }

      // Update state with the processed data
      setConfigurations(data)
      selectDiameters(data)

      // Restore previously selected values if they exist (use local widthMap instead of state)
      if (typedValue?.diameter?.name) {
        setSelectedDiameter(typedValue.diameter.name)

        // Use more flexible filtering to handle potential type/format mismatches
        const widthsInput = data.filter(({ diameter }) => {
          return (
            String(diameter) === String(typedValue.diameter!.name) ||
            parseFloat(diameter) === parseFloat(typedValue.diameter!.name)
          )
        })

        selectWidths(widthsInput)

        // Handle width restoration - width is now a reference (use local widthMap)
        if (typedValue?.width?._ref) {
          // Find the width document by ID and get its name using local widthMap
          const savedWidthDoc = [...widthMap.values()].find(
            (w) => w._id === typedValue.width!._ref,
          )
          if (savedWidthDoc) {
            // Create a local function to find width document by value using the local map
            const findWidthByValue = (magentoWidth: string): WidthDocument | null => {
              if (!magentoWidth) return null

              // First try exact match
              let widthDoc = [...widthMap.values()].find((w) => w.name === magentoWidth)
              if (widthDoc) return widthDoc

              // Extract numeric value for comparison
              const numericValue = parseFloat(magentoWidth)
              if (isNaN(numericValue)) return null

              // Try different format variations
              const variations = [
                magentoWidth,
                `${numericValue}"`,
                `${numericValue}`,
                numericValue.toFixed(1),
                `${numericValue.toFixed(1)}"`,
                numericValue % 1 === 0
                  ? `${Math.floor(numericValue)}`
                  : `${numericValue}`,
              ]

              for (const variant of variations) {
                widthDoc = [...widthMap.values()].find((w) => w.name === variant)
                if (widthDoc) return widthDoc
              }

              // Loose numeric matching
              for (const width of widthMap.values()) {
                const storedNumeric = parseFloat(width.name)
                if (
                  !isNaN(storedNumeric) &&
                  Math.abs(storedNumeric - numericValue) < 0.01
                ) {
                  return width
                }
              }

              return null
            }

            // Find the Magento width that matches this Sanity width document
            const offsetInput = widthsInput.filter(({ width }) => {
              // Try to match Magento width (e.g., "9.0") with Sanity width name (e.g., "9")
              const widthDoc = findWidthByValue(width)
              const match = widthDoc && widthDoc._id === savedWidthDoc._id
              if (match) {
                // Set the selected width to the Magento value (what's shown in UI)
                setSelectedWidth(width)
              }
              return match
            })
            selectOffsets(offsetInput)
            // Continue with offset restoration for reference format
            if (typedValue?.offset?.name) {
              setSelectedOffset(typedValue.offset.name)

              const filteredData = offsetInput.filter(
                ({ offset }) => offset !== typedValue.offset!.name,
              )
              selectAlternativeOffsets(filteredData, typedValue.offset.name)

              if (typedValue?.alternativeOffset?.name) {
                setSelectedAlternativeOffset(typedValue.alternativeOffset.name)
              }
            }
          }
        }
      }

      setIsLoading(false)
    } catch (error: any) {
      setError(
        error.message ||
          'Failed to load wheel sizes. Please check your connection and try again.',
      )
      setIsLoading(false)
    }
  }, [
    document?._id,
    typedValue,
    client,
    selectDiameters,
    selectWidths,
    selectOffsets,
    selectAlternativeOffsets,
  ])

  useEffect(() => {
    if (document?._id) {
      loadWheelData()
    }
  }, [retryAttempt, document?._id])

  const handleRetry = () => {
    setRetryAttempt((prev) => prev + 1)
  }

  return (
    <Stack gap={3} ref={ref}>
      <Box>
        <Label size={1}>{schemaType.title}</Label>
        {schemaType.description && (
          <Box paddingTop={2}>
            <Label size={0} muted>
              {schemaType.description}
            </Label>
          </Box>
        )}
      </Box>

      <Stack gap={3}>
        {isLoading ? (
          <Flex align="center" justify="center" padding={4}>
            <Spinner muted />
            <Box marginLeft={3}>
              <Text>Loading data...</Text>
            </Box>
          </Flex>
        ) : error ? (
          <Card tone="critical" padding={4}>
            <Stack gap={3}>
              <Text size={1} weight="semibold">
                Error Loading Wheel Sizes
              </Text>
              <Text size={1} muted>
                {error}
              </Text>
              <Button
                tone="critical"
                text="Retry"
                onClick={handleRetry}
                icon={
                  <svg
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 12C4 7.58172 7.58172 4 12 4C14.3894 4 16.5341 5.04409 18 6.67133M20 12C20 16.4183 16.4183 20 12 20C9.61061 20 7.46589 18.9559 6 17.3287M1 8L4 5L7 8M17 16L20 19L23 16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              />
              <details>
                <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#666' }}>
                  Troubleshooting Tips
                </summary>
                <Box marginTop={2}>
                  <Text size={0} muted>
                    • Ensure the frontend server is running (npm run dev)
                    <br />
                    • Check that the API endpoint is accessible
                    <br />
                    • Verify network connectivity
                    <br />
                    • Check browser console for detailed errors
                    <br />• Contact support if the issue persists
                  </Text>
                </Box>
              </details>
            </Stack>
          </Card>
        ) : (
          <Stack gap={4}>
            {/* Selectors */}
            <Grid gridTemplateColumns={4} gap={3}>
              <Stack gap={2}>
                <Heading size={0}>Wheel Diameter</Heading>
                <Select
                  value={selectedDiameter}
                  onChange={(event) => selectDiameter(event.currentTarget.value)}
                >
                  <option value="">Select Diameter</option>
                  {diameters.map((diameter) => (
                    <option key={diameter} value={diameter}>
                      {diameter}
                    </option>
                  ))}
                </Select>
              </Stack>
              <Stack gap={2}>
                <Heading size={0}>Wheel Width</Heading>
                <Select
                  value={selectedWidth}
                  onChange={(event) => selectWidth(event.currentTarget.value)}
                >
                  <option value="">Select Width</option>
                  {widths.map((width) => (
                    <option key={width} value={width}>
                      {width}
                    </option>
                  ))}
                </Select>
              </Stack>
              <Stack gap={2}>
                <Heading size={0}>Wheel Offset</Heading>
                <Select
                  value={selectedOffset}
                  onChange={(event) => selectOffset(event.currentTarget.value)}
                >
                  <option value="">Select Offset</option>
                  {offsets.map((offset) => (
                    <option key={offset} value={offset}>
                      {offset}
                    </option>
                  ))}
                </Select>
              </Stack>
              <Stack gap={2}>
                <Heading size={0}>Alternative Offset</Heading>
                <Select
                  value={selectedAlternativeOffset}
                  onChange={(event) =>
                    selectAlternativeOffset(event.currentTarget.value)
                  }
                  disabled={!selectedOffset}
                >
                  <option value="">Select Alternative Offset</option>
                  {alternativeOffsets.map((offset) => (
                    <option key={offset} value={offset}>
                      {offset}
                    </option>
                  ))}
                </Select>
              </Stack>
            </Grid>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
})

WheelSizeSelector.displayName = 'WheelSizeSelector'

export default WheelSizeSelector
